/** Istio TCP exposure for database environments (database-manager /istio/db-gateway/*). */

export interface IRemediation {
  repo: string;
  file: string;
  instructions: string;
  suggested_yaml: {
    service_ports: string;
    gateway_servers: string;
  };
}

export interface IPortUsage {
  port: number;
  virtual_service: string;
  namespace: string;
  destination?: string | null;
  env_id?: number | null;
  /** False for hand-made VirtualServices: shown, but never acted on from here. */
  managed: boolean;
}

export interface IPortCapacity {
  gateway: string;
  address?: string | null;
  rbac_ok: boolean;
  rbac_error?: string | null;
  declared: number[];
  declared_count: number;
  used: IPortUsage[];
  used_count: number;
  free: number[];
  free_count: number;
  /** Free ports never allocated before; preferred over recycled ones. */
  fresh: number[];
  fresh_count: number;
  recycled: number[];
  /** Held by an in-flight allocation. Excluded from `free`. */
  reserved: number[];
  /** `free` ignoring in-flight reservations — what the range holds once contention clears. */
  unreserved: number[];
  /** Authoritative: only `exhausted` means the range needs extending. */
  exhausted: boolean;
  /** Ports exist but are all momentarily reserved. Clears on its own. */
  contended: boolean;
  watermark: number;
  next_port?: number | null;
  low_watermark: boolean;
  gateway_only: number[];
  service_only: number[];
  undeclared_used: number[];
  remediation?: IRemediation | null;
}

export interface IExposure {
  env_id?: number | null;
  name: string;
  namespace: string;
  port: number;
  destination?: string | null;
  gateway: string;
  managed: boolean;
  connection_hint?: string | null;
}

export type PreflightReason =
  | "ports_exhausted"
  | "ports_contended"
  | "already_exposed"
  | "name_taken"
  | "invalid_name"
  | "rbac_denied";

export interface IPreflight {
  can_expose: boolean;
  gateway?: string | null;
  reason?: PreflightReason | null;
  message?: string | null;
  name?: string | null;
  next_port?: number | null;
  connection_hint?: string | null;
  free_count: number;
  fresh_count: number;
  declared_count: number;
  low_watermark: boolean;
  existing?: IExposure | null;
  remediation?: IRemediation | null;
}

export type ExposureState =
  | "exposed"
  | "exposed_unmanaged"
  | "not_exposed"
  | "not_exposed_no_ports";

/** Mirrors the server rule; the server revalidates, this only avoids a round trip. */
export const EXPOSURE_NAME_PREFIX = "tsdb-internal-";
export const MAX_EXPOSURE_NAME_LENGTH = 63 - EXPOSURE_NAME_PREFIX.length;
const RFC1123_LABEL = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;

export const slugifyExposureName = (value: string): string =>
  (value || "")
    .normalize("NFKD")
    // eslint-disable-next-line no-control-regex
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_EXPOSURE_NAME_LENGTH)
    .replace(/-+$/g, "");

export const validateExposureName = (value: string): string | null => {
  if (!value) return "Exposure name must not be empty";
  if (value.length > MAX_EXPOSURE_NAME_LENGTH) {
    return `Exposure name must be at most ${MAX_EXPOSURE_NAME_LENGTH} characters`;
  }
  if (!RFC1123_LABEL.test(value)) {
    return "Use lowercase letters, digits and '-', starting and ending with a letter or digit";
  }
  return null;
};
